import { useState, useEffect, useMemo } from "react";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";

type ActiveCoupon =
	RouterInferenceHelpers["premium"]["getAllProductsPrices"]["output"]["active_coupon"];

interface SaleBannerProps {
	coupon: ActiveCoupon;
}

function useCountdown(endDate: Date | null) {
	const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(endDate));

	useEffect(() => {
		if (!endDate) return;

		const interval = setInterval(() => {
			setTimeLeft(calculateTimeLeft(endDate));
		}, 1000);

		return () => clearInterval(interval);
	}, [endDate]);

	return timeLeft;
}

function calculateTimeLeft(endDate: Date | null) {
	if (!endDate) return null;

	const now = new Date().getTime();
	const end = new Date(endDate).getTime();
	const diff = end - now;

	if (diff <= 0) {
		return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
	}

	return {
		days: Math.floor(diff / (1000 * 60 * 60 * 24)),
		hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
		minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
		seconds: Math.floor((diff % (1000 * 60)) / 1000),
		expired: false,
	};
}

export function SaleBanner({ coupon }: SaleBannerProps) {
	if (!coupon) return null;

	const endDate = coupon.end_date ? new Date(coupon.end_date) : null;
	const timeLeft = useCountdown(endDate);

	// Don't show if expired
	if (timeLeft?.expired) return null;

	const discountText = coupon.api_data.percent_off
		? `${coupon.api_data.percent_off}% OFF`
		: `$${(coupon.api_data.amount_off / 100).toFixed(0)} OFF`;

	const couponName = coupon.api_data.name || "Limited Time Sale";

	return (
		<div className="sale-banner-wrapper">
			<div className="sale-banner">
				{/* Content */}
				<div className="sale-banner-content">
					{/* Left: Sale info */}
					<div className="sale-banner-info">
						<div className="sale-banner-badge">
							<span className="sale-banner-badge-icon">
								<i className="fas fa-bolt"></i>
							</span>
							<span className="sale-banner-badge-text">LIMITED TIME</span>
						</div>
						<h2 className="sale-banner-title">{couponName}</h2>
						<div className="sale-banner-discount">{discountText}</div>
					</div>

					{/* Right: Countdown */}
					{timeLeft && !timeLeft.expired && (
						<div className="sale-banner-countdown">
							<div className="sale-banner-countdown-label">ENDS IN</div>
							<div className="sale-banner-countdown-timer">
								<CountdownUnit value={timeLeft.days} label="DAYS" />
								<span className="sale-banner-countdown-sep">:</span>
								<CountdownUnit value={timeLeft.hours} label="HRS" />
								<span className="sale-banner-countdown-sep">:</span>
								<CountdownUnit value={timeLeft.minutes} label="MIN" />
								<span className="sale-banner-countdown-sep">:</span>
								<CountdownUnit value={timeLeft.seconds} label="SEC" />
							</div>
						</div>
					)}
				</div>
			</div>

			<style>{`
				.sale-banner-wrapper {
					--banner-primary: #00f4ff;
					--banner-secondary: #e2e608;
					--banner-bg-dark: #0f1318;
					--banner-bg-medium: #161b22;
					display: flex;
					justify-content: center;
					width: 100%;
					padding: 0 0.25rem;
					margin-bottom: 1.5rem;
				}

				.sale-banner {
					position: relative;
					overflow: hidden;
					border-radius: 8px;
					background: var(--banner-bg-dark);
					padding: 0.875rem 1.25rem;
					width: 100%;
					max-width: calc(3 * 20rem + 2 * 0.5rem);
					border: 1px solid transparent;
					background-image: linear-gradient(var(--banner-bg-dark), var(--banner-bg-dark)),
						linear-gradient(135deg, rgba(0, 244, 255, 0.4) 0%, rgba(226, 230, 8, 0.3) 50%, rgba(0, 244, 255, 0.4) 100%);
					background-origin: border-box;
					background-clip: padding-box, border-box;
				}

				/* Content layout */
				.sale-banner-content {
					position: relative;
					display: flex;
					align-items: center;
					justify-content: space-between;
					gap: 1.5rem;
					flex-wrap: wrap;
				}

				/* Sale info section */
				.sale-banner-info {
					display: flex;
					flex-direction: column;
					gap: 0.25rem;
				}

				.sale-banner-badge {
					display: inline-flex;
					align-items: center;
					gap: 0.375rem;
					padding: 0.25rem 0.625rem;
					background: rgba(226, 230, 8, 0.15);
					border: 1px solid rgba(226, 230, 8, 0.4);
					border-radius: 4px;
					width: fit-content;
				}

				.sale-banner-badge-icon {
					color: var(--banner-secondary);
					font-size: 0.625rem;
					animation: flash 1.5s ease-in-out infinite;
				}

				@keyframes flash {
					0%, 100% { opacity: 1; }
					50% { opacity: 0.5; }
				}

				.sale-banner-badge-text {
					font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
					font-size: 0.625rem;
					font-weight: 700;
					letter-spacing: 0.1em;
					color: var(--banner-secondary);
					text-transform: uppercase;
				}

				.sale-banner-title {
					font-family: system-ui, -apple-system, sans-serif;
					font-size: 1.125rem;
					font-weight: 700;
					color: #fff;
					margin: 0;
					line-height: 1.3;
				}

				.sale-banner-discount {
					font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
					font-size: 1.75rem;
					font-weight: 800;
					background: linear-gradient(135deg, var(--banner-primary) 0%, #fff 50%, var(--banner-primary) 100%);
					background-size: 200% auto;
					-webkit-background-clip: text;
					background-clip: text;
					-webkit-text-fill-color: transparent;
					animation: shimmer 3s linear infinite;
					line-height: 1.2;
					letter-spacing: -0.02em;
				}

				@keyframes shimmer {
					0% { background-position: -200% center; }
					100% { background-position: 200% center; }
				}

				/* Countdown section */
				.sale-banner-countdown {
					display: flex;
					flex-direction: column;
					align-items: flex-end;
					gap: 0.375rem;
				}

				.sale-banner-countdown-label {
					font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
					font-size: 0.625rem;
					font-weight: 600;
					letter-spacing: 0.15em;
					color: rgba(255, 255, 255, 0.5);
				}

				.sale-banner-countdown-timer {
					display: flex;
					align-items: center;
					gap: 0.25rem;
				}

				.sale-banner-countdown-sep {
					font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
					font-size: 1.25rem;
					font-weight: 700;
					color: rgba(0, 244, 255, 0.5);
					margin-bottom: 1rem;
				}

				.countdown-unit {
					display: flex;
					flex-direction: column;
					align-items: center;
					min-width: 3.25rem;
				}

				.countdown-value {
					font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
					font-size: 1.5rem;
					font-weight: 700;
					color: #fff;
					background: rgba(0, 244, 255, 0.1);
					border: 1px solid rgba(0, 244, 255, 0.2);
					border-radius: 6px;
					padding: 0.25rem 0.5rem;
					min-width: 2.75rem;
					text-align: center;
					line-height: 1.3;
					box-shadow:
						0 0 20px rgba(0, 244, 255, 0.1),
						inset 0 1px 0 rgba(255, 255, 255, 0.05);
				}

				.countdown-label {
					font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
					font-size: 0.5rem;
					font-weight: 600;
					letter-spacing: 0.1em;
					color: rgba(255, 255, 255, 0.4);
					margin-top: 0.25rem;
				}

				/* Mobile responsive */
				@media (max-width: 640px) {
					.sale-banner {
						padding: 1rem;
					}

					.sale-banner-content {
						flex-direction: column;
						align-items: center;
						text-align: center;
						gap: 1rem;
					}

					.sale-banner-info {
						align-items: center;
					}

					.sale-banner-countdown {
						align-items: center;
					}

					.sale-banner-discount {
						font-size: 1.5rem;
					}

					.countdown-value {
						font-size: 1.25rem;
						min-width: 2.25rem;
						padding: 0.25rem 0.375rem;
					}

					.countdown-unit {
						min-width: 2.5rem;
					}
				}
			`}</style>
		</div>
	);
}

function CountdownUnit({ value, label }: { value: number; label: string }) {
	return (
		<div className="countdown-unit">
			<div className="countdown-value">{String(value).padStart(2, "0")}</div>
			<div className="countdown-label">{label}</div>
		</div>
	);
}
